/**
 * @fileoverview API configuration and server URL resolution.
 *
 * Determines the backend server URL based on:
 * 1. ?server= query parameter (for development/testing)
 * 2. window.appConfig.BASE_URL (for deployed environments)
 * 3. localhost:3000 (for local development)
 * 4. User prompt (fallback)
 *
 * @module helpers/apiConfig
 */

import _ from "lodash";
import { isLocal } from "@/helperMethods/util";

/** @type {URLSearchParams} URL query parameters */
const params = new URLSearchParams(location.search);

/**
 * Resolves the backend server root URL.
 *
 * URL resolution priority:
 * 1. ?server= query param (supports full URLs, subdomains, or shortnames)
 * 2. window.appConfig.BASE_URL from deployment config
 * 3. http://localhost:3000 for local development
 * 4. Prompts user for URL as last resort
 *
 * Server param formats:
 * - Full URL: "https://example.com/server/"
 * - App domain: "myapp.app" -> "https://myapp.app.keyreply.com/server/"
 * - Shortname: "myapp" -> "https://myapp.staging.keyreply.com/server/"
 *
 * @returns {string} The resolved server root URL
 *
 * @example
 * // Query param: ?server=https://api.example.com/server/
 * getRoot(); // Returns: 'https://api.example.com/server/'
 *
 * // Query param: ?server=myapp
 * getRoot(); // Returns: 'https://myapp.staging.keyreply.com/server/'
 */
const getRoot = (): string => {
  const server = params.get("server");
  const baseURL = _.get(window, "appConfig.BASE_URL");

  if (server) {
    if (/^http/.test(server)) {
      if (/\/server/.test(server)) {
        return server;
      } else {
        return `${server}/server/`;
      }
    } else if (/\.(app|staging)/.test(server)) {
      if (/.keyreply.com$/.test(server)) {
        return `https://${server}/server/`;
      } else {
        return `https://${server}.keyreply.com/server/`;
      }
    } else {
      return `https://${server}.staging.keyreply.com/server/`;
    }
  } else {
    if (baseURL) {
      return baseURL;
    }

    if (isLocal()) {
      return `http://localhost:3000`;
    }
  }

  return prompt("Server URL") || "";
};

/**
 * API configuration object containing the server root URL.
 *
 * @type {Object}
 * @property {string} root - The resolved server root URL
 *
 * @example
 * import { config } from '@/helpers/apiConfig';
 * fetch(`${config.root}/api/endpoint`);
 */
const config = {
  root: getRoot(),
};

export { config, getRoot };
